<template>
  <div class="form-input" ref="containerRef">
    <div class="wrapper-label">
      <label class="label-input" :class="{ 'label-disabled': isDisabled }">
        {{ label }}
      </label>
    </div>

    <div
      class="custom-select"
      :class="{ disabled: isDisabled }"
      @click="toggleDropdown"
    >
      <div class="selected-option">
        {{ selectedLabel || (isDisabled ? "Deshabilitado" : placeholderText) }}
      </div>
      <img
        class="select-arrow"
        src="assets/administrator/DownArrow.svg"
        alt="arrow"
        :class="{ rotated: isOpen }"
      />
      <ul v-show="isOpen && options?.length" class="select-options">
        <li
          v-for="(option, index) in options"
          :key="index"
          class="option-item"
          @click.stop="selectOption(option)"
        >
          {{ option.value }}
        </li>
      </ul>
    </div>

    <p :class="hasError ? 'error' : 'no-error'">
      {{ hasError ? error.esMessage : "" }}
    </p>
  </div>
</template>

<script lang="ts">
import {
  defineComponent,
  ref,
  computed,
  watch,
  onMounted,
  onUnmounted,
} from "vue";

interface SelectOption {
  id: string;
  value: string;
}

interface MessageError {
  message?: string;
}

export default defineComponent({
  name: "AdminCustomSelect",
  props: {
    label: {
      type: String,
      default: "",
    },
    isDisabled: {
      type: Boolean,
      default: false,
    },
    options: {
      type: Array as () => SelectOption[],
      required: true,
      default: () => [],
    },
    value: {
      type: String,
      default: "",
    },
    placeholder: {
      type: String,
      default: "",
    },
    error: {
      type: Object as () => MessageError | string,
      default: null,
    },
    hasError: {
      type: Boolean,
      default: false,
    },
  },
  emits: ["update:text", "changeOption"],
  setup(props, { emit }) {
    const isOpen = ref(false);
    const optionSelected = ref(props.value || "");
    const containerRef = ref<HTMLElement | null>(null);
    const placeholderText = ref(props.placeholder);

    const selectedLabel = computed(() => {
      if (!optionSelected.value || !props.options?.length) return "";
      const selected = props.options.find(
        (opt) => opt.id === optionSelected.value
      );
      return selected?.value || "";
    });

    const toggleDropdown = () => {
      if (!props.isDisabled && props.options?.length) {
        isOpen.value = !isOpen.value;
      }
    };

    const closeDropdown = () => {
      isOpen.value = false;
    };

    const selectOption = (option: SelectOption) => {
      if (!option) return;
      optionSelected.value = option.id;
      emit("update:text", option.id);
      emit("changeOption", option.id);
      closeDropdown();
    };

    watch(
      () => props.value,
      (newVal) => {
        optionSelected.value = newVal || "";
      },
      { immediate: true }
    );

    watch(
      () => props.options,
      (newOptions) => {
        if (!newOptions?.length) {
          closeDropdown();
        }
      }
    );

    const handleClickOutside = (event: MouseEvent) => {
      if (
        containerRef.value &&
        !containerRef.value.contains(event.target as Node)
      ) {
        closeDropdown();
      }
    };

    onMounted(() => {
      document.addEventListener("click", handleClickOutside);
    });

    onUnmounted(() => {
      document.removeEventListener("click", handleClickOutside);
    });

    return {
      isOpen,
      optionSelected,
      toggleDropdown,
      closeDropdown,
      selectOption,
      selectedLabel,
      containerRef,
      placeholderText,
    };
  },
});
</script>

<style scoped lang="scss">
@import "src/generalStyle/controls/adminSelect";
</style>